import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin, Star, Calculator } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES, CONTACT, waLink } from "@/data/site";

export const Hero = () => {
  const { t } = useLang();
  const h = t.hero;

  const go = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Interior de vehículo restaurado"
          className="w-full h-full object-cover"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
      </div>

      <div className="container-x relative z-10 pt-28 pb-20">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-light"
            data-testid="hero-badge"
          >
            <span className="h-2 w-2 rounded-full bg-brand animate-pulse" />
            {h.badge}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 font-heading font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            data-testid="hero-title"
          >
            {h.title1}{" "}
            <span className="text-brand">{h.title2}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-base sm:text-lg text-zinc-300 max-w-xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            {h.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button
              onClick={() => go("cotizador")}
              data-testid="hero-quote-btn"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold text-base transition-all hover:-translate-y-0.5 shadow-lg shadow-brand/25"
            >
              <Calculator className="h-5 w-5" />
              {h.cta1}
            </button>
            <a
              href={`tel:+${CONTACT.phone1Raw}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-white/5 hover:bg-white/10 border border-white/20 text-white font-semibold text-base transition-all hover:-translate-y-0.5"
            >
              <Phone className="h-5 w-5" />
              {h.cta2}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-10 flex items-center gap-2 text-sm text-zinc-400"
            data-testid="hero-area"
          >
            <MapPin className="h-4 w-4 text-brand" />
            {h.area}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-8 grid grid-cols-3 gap-4 sm:gap-8 max-w-lg border-t border-white/10 pt-6"
          >
            <Stat value="+8" label={h.stat1} />
            <Stat value="+500" label={h.stat2} />
            <Stat
              value={
                <span className="inline-flex items-center gap-1">
                  5.0 <Star className="h-4 w-4 fill-brand text-brand" />
                </span>
              }
              label={h.stat3}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <div className="font-heading text-2xl sm:text-3xl font-bold text-white">
      {value}
    </div>
    <div className="text-xs text-zinc-400 mt-1 leading-tight">{label}</div>
  </div>
);
